/* Artzy Personalization — per-photo upload interaction.
   Handles preview/remove/replace/validate for the multi-slot upload
   groups rendered by snippets/artzy-personalization-field-row.liquid.
   Entirely frontend: nothing is uploaded to Shopify until Add to Cart.
   Slot visuals are driven entirely by data-state on [data-artzy-upload-slot]:
   empty | dragover | uploading | complete | failed | invalid | disabled */
(() => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB — matches the states-grid spec's "Max 5MB" validation state

  class ArtzyPersonalization {
    static init(root = document) {
      root.querySelectorAll('[data-artzy-upload-group]').forEach((group) => this.initUploadGroup(group));

      this.initLiveValidationClearing(root);
      this.initCharacterCounters(root);
    }

    static initUploadGroup(group) {
      if (group.dataset.artzyInitialized === 'true') return;
      group.dataset.artzyInitialized = 'true';

      group.querySelectorAll('[data-artzy-upload-slot]').forEach((slot) => {
        const input = slot.querySelector('[data-artzy-upload-input]');
        const dropzone = slot.querySelector('[data-artzy-upload-dropzone]');
        const removeButton = slot.querySelector('[data-artzy-upload-remove]');
        const hint = slot.querySelector('[data-artzy-upload-hint]');

        slot._artzyDefaultHint = hint ? hint.textContent : '';

        input?.addEventListener('change', () => {
          this.handleFileChange(slot);
        });

        removeButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.clearSlot(slot);
        });

        ['dragenter', 'dragover'].forEach((type) => {
          dropzone?.addEventListener(type, (event) => {
            if (slot.dataset.state === 'disabled') return;
            event.preventDefault();
            if (slot.dataset.state !== 'complete' && slot.dataset.state !== 'uploading') {
              slot.dataset.state = 'dragover';
            }
          });
        });

        ['dragleave', 'dragend'].forEach((type) => {
          dropzone?.addEventListener(type, () => {
            if (slot.dataset.state === 'dragover') slot.dataset.state = 'empty';
          });
        });

        dropzone?.addEventListener('drop', (event) => {
          if (slot.dataset.state === 'disabled') return;
          event.preventDefault();
          const file = event.dataTransfer?.files?.[0];
          if (file && input) {
            const transfer = new DataTransfer();
            transfer.items.add(file);
            input.files = transfer.files;
            this.handleFileChange(slot);
          } else if (slot.dataset.state === 'dragover') {
            slot.dataset.state = 'empty';
          }
        });
      });

      this.updateGroup(group);
    }

    static handleFileChange(slot) {
      const input = slot.querySelector('[data-artzy-upload-input]');
      const file = input?.files?.[0];

      if (!file) {
        this.clearSlot(slot);
        return;
      }

      const group = slot.closest('[data-artzy-upload-group]');
      const allowed = (group.dataset.allowedTypes || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);

      // allowed_file_types stores full MIME types (e.g. "image/jpeg") for
      // most fields, but accept either MIME type or bare extension so this
      // keeps working regardless of which convention a given field uses.
      if (allowed.length) {
        const fileType = (file.type || '').toLowerCase();
        const extension = file.name.split('.').pop()?.toLowerCase();
        const isAllowed = allowed.some((item) =>
          item.includes('/') ? item === fileType : item.replace(/^\./, '') === extension
        );

        if (!isAllowed) {
          input.value = '';
          const readable = allowed
            .map((item) => (item.includes('/') ? item.split('/').pop() : item).toUpperCase())
            .join(', ');
          this.setSlotInvalid(slot, `That file type isn't supported — please upload a ${readable} photo instead.`);
          return;
        }
      }

      if (file.size > MAX_FILE_SIZE) {
        input.value = '';
        this.setSlotInvalid(slot, "That photo is too large — please upload one under 5MB.");
        return;
      }

      slot.dataset.state = 'uploading';
      const percent = slot.querySelector('[data-artzy-upload-percent]');
      if (percent) percent.textContent = '0%';

      let progress = 0;
      const timer = window.setInterval(() => {
        progress = Math.min(progress + 34, 100);
        if (percent) percent.textContent = `${progress}%`;
        if (progress >= 100) {
          window.clearInterval(timer);
          this.renderPreview(slot, file);
          slot.dataset.state = 'complete';
          this.updateGroup(slot.closest('[data-artzy-upload-group]'));
        }
      }, 90);
    }

    static setSlotInvalid(slot, message) {
      slot.dataset.state = 'invalid';
      const hint = slot.querySelector('[data-artzy-upload-hint]');
      if (hint) hint.textContent = message;
      const group = slot.closest('[data-artzy-upload-group]');
      this.showError(group, message);
      this.updateGroup(group);
    }

    static renderPreview(slot, file) {
      const preview = slot.querySelector('[data-artzy-upload-preview]');

      if (slot._artzyPreviewUrl) {
        URL.revokeObjectURL(slot._artzyPreviewUrl);
        slot._artzyPreviewUrl = null;
      }

      if (file.type.startsWith('image/')) {
        const objectUrl = URL.createObjectURL(file);
        slot._artzyPreviewUrl = objectUrl;
        preview.src = objectUrl;
      } else if (preview) {
        preview.removeAttribute('src');
      }
    }

    static clearSlot(slot) {
      const input = slot.querySelector('[data-artzy-upload-input]');
      const preview = slot.querySelector('[data-artzy-upload-preview]');
      const hint = slot.querySelector('[data-artzy-upload-hint]');
      const percent = slot.querySelector('[data-artzy-upload-percent]');

      if (input) input.value = '';

      if (slot._artzyPreviewUrl) {
        URL.revokeObjectURL(slot._artzyPreviewUrl);
        slot._artzyPreviewUrl = null;
      }

      if (preview) preview.removeAttribute('src');
      if (hint) hint.textContent = slot._artzyDefaultHint || '';
      if (percent) percent.textContent = '';

      slot.dataset.state = 'empty';

      const group = slot.closest('[data-artzy-upload-group]');
      this.updateGroup(group);
    }

    static updateGroup(group) {
      if (!group) return;

      const inputs = [...group.querySelectorAll('[data-artzy-upload-input]')];
      const selectedCount = inputs.filter((input) => input.files?.length).length;

      const minimum = Number(group.dataset.uploadMin) || 0;
      const maximum = Number(group.dataset.uploadMax) || inputs.length;

      const countElement = group.querySelector('[data-artzy-upload-count]');

      if (countElement) {
        countElement.textContent = `${selectedCount} / ${maximum}`;
      }

      group.dataset.valid = selectedCount >= minimum && selectedCount <= maximum ? 'true' : 'false';

      if (group.dataset.valid === 'true') {
        group.classList.remove('is-invalid');
        this.hideError(group);
      }
    }

    static showError(group, message) {
      const error = group.querySelector('[data-artzy-upload-error]');
      group.classList.add('is-invalid');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    static hideError(group) {
      const error = group.querySelector('[data-artzy-upload-error]');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    static validateForm(form) {
      let valid = true;
      let firstInvalidInput = null;
      const missingLabels = [];

      // form.id can be shadowed by a named form control (Dawn's product
      // forms have <input name="id"> for the variant id), which makes the
      // .id property return that element instead of the form's own id
      // attribute. getAttribute('id') always returns the real attribute.
      const formId = form.getAttribute('id');

      this.clearFieldHighlights(form);

      document.querySelectorAll('[data-artzy-upload-group]').forEach((group) => {
        if (group.dataset.formId !== formId) {
          return;
        }

        this.updateGroup(group);

        if (group.dataset.valid !== 'true') {
          valid = false;

          const minimum = Number(group.dataset.uploadMin) || 0;
          const selected = [...group.querySelectorAll('[data-artzy-upload-input]')].filter(
            (input) => input.files?.length
          ).length;
          const remaining = Math.max(minimum - selected, 0);

          this.showError(
            group,
            `Please upload ${remaining === 1 ? '1 more photo' : `${remaining} more photos`} before adding this product to your cart.`
          );

          missingLabels.push(group.dataset.uploadLabel || 'Photos');
          group.closest('.artzy-personalization__row')?.classList.add('artzy-field-invalid');

          if (!firstInvalidInput) {
            firstInvalidInput =
              group.querySelector('[data-artzy-upload-input]:not(:valid)') ||
              group.querySelector('[data-artzy-upload-input]');
          }
        }
      });

      // Every other required control (text, textarea, select, date, checkbox...)
      // in this form: flag ALL of them, not just the first — a single native
      // tooltip is easy to miss on a long form (e.g. a native date input),
      // which is exactly what silently blocked Add to Cart for a real user.
      [...form.elements]
        .filter((element) => typeof element.checkValidity === 'function' && !element.closest('[data-artzy-upload-group]'))
        .forEach((element) => {
          if (element.checkValidity()) return;

          valid = false;
          if (!firstInvalidInput) firstInvalidInput = element;

          const row = element.closest('.artzy-personalization__row');
          if (row) {
            row.classList.add('artzy-field-invalid');
            const label = row.querySelector('.artzy-personalization__label');
            // textContent on the label also picks up the nested character
            // counter ("0/30") -- read only the label's own text nodes.
            const labelOwnText = label
              ? [...label.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('')
              : '';
            const labelText = (labelOwnText || label?.textContent || '').trim().replace(/\*$/, '').trim() || 'This field';
            missingLabels.push(labelText);

            const fieldError = row.querySelector('[data-artzy-field-error]');
            if (fieldError) {
              // Names the fix, not just the problem (sheet 13, rule: every
              // error names the fix) -- reuses the field's own worked-
              // example placeholder ("e.g. 15/08/2024") where one exists,
              // same pattern as "Enter an email address like name@example.com".
              let message;
              if (element.validity.valueMissing) {
                const example = element.placeholder ? ` (${element.placeholder})` : '';
                if (element.tagName === 'SELECT') {
                  message = `Choose ${labelText}${example}.`;
                } else if (element.type === 'checkbox' || element.type === 'radio') {
                  message = `Select ${labelText} to continue.`;
                } else {
                  message = `Enter ${labelText}${example}.`;
                }
              } else {
                message = element.validationMessage || `${labelText} isn't valid yet.`;
              }
              fieldError.textContent = message;
              fieldError.hidden = false;
            }
          } else {
            const label = element.labels?.[0];
            const labelText = label?.textContent.trim() || element.name || 'This field';
            missingLabels.push(labelText);
            const confirm = label?.classList.contains('artzy-personalization__confirm--required') ? label : null;
            const fieldError = confirm?.querySelector('[data-artzy-field-error]');
            if (confirm && fieldError) {
              confirm.classList.add('artzy-confirm-invalid');
              fieldError.textContent = 'Please check this box to continue.';
              fieldError.hidden = false;
            }
          }
        });

      this.renderValidationSummary(form, valid ? [] : [...new Set(missingLabels)]);

      if (!valid) {
        firstInvalidInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalidInput?.focus({ preventScroll: true });
      }

      return valid;
    }

    static clearFieldHighlights(form) {
      document.querySelectorAll('.artzy-field-invalid').forEach((row) => {
        row.classList.remove('artzy-field-invalid');
        const fieldError = row.querySelector('[data-artzy-field-error]');
        if (fieldError) {
          fieldError.hidden = true;
          fieldError.textContent = '';
        }
      });
      document.querySelectorAll('.artzy-confirm-invalid').forEach((confirm) => {
        confirm.classList.remove('artzy-confirm-invalid');
        const fieldError = confirm.querySelector('[data-artzy-field-error]');
        if (fieldError) {
          fieldError.hidden = true;
          fieldError.textContent = '';
        }
      });
      const summary = form.querySelector('[data-artzy-validation-summary]');
      if (summary) {
        summary.hidden = true;
        summary.textContent = '';
      }
    }

    static renderValidationSummary(form, labels) {
      let summary = form.querySelector('[data-artzy-validation-summary]');

      if (!labels.length) {
        if (summary) {
          summary.hidden = true;
          summary.textContent = '';
        }
        return;
      }

      if (!summary) {
        summary = document.createElement('p');
        summary.setAttribute('data-artzy-validation-summary', '');
        summary.className = 'artzy-validation-summary';
        summary.setAttribute('role', 'alert');
        const submitButton = form.querySelector('.product-form__buttons, [name="add"]');
        (submitButton?.closest('.product-form__buttons') || submitButton)?.insertAdjacentElement('beforebegin', summary);
      }

      summary.hidden = false;
      summary.textContent = `Please complete before adding to cart: ${labels.join(', ')}.`;
    }

    static initLiveValidationClearing(root) {
      root.querySelectorAll('.artzy-personalization').forEach((container) => {
        if (container.dataset.artzyValidationInit === 'true') return;
        container.dataset.artzyValidationInit = 'true';

        container.addEventListener('input', (event) => this.clearRowIfValid(event.target));
        container.addEventListener('change', (event) => this.clearRowIfValid(event.target));
      });
    }

    static clearRowIfValid(field) {
      if (typeof field.checkValidity !== 'function' || !field.checkValidity()) return;

      const row = field.closest('.artzy-personalization__row');
      const confirm = !row
        ? field.closest('.artzy-personalization__confirm--required.artzy-confirm-invalid')
        : null;

      if (!row && !confirm) return;
      if (row && !row.classList.contains('artzy-field-invalid')) return;

      const target = row || confirm;
      target.classList.remove('artzy-field-invalid', 'artzy-confirm-invalid');
      const fieldError = target.querySelector('[data-artzy-field-error]');
      if (fieldError) {
        fieldError.hidden = true;
        fieldError.textContent = '';
      }

      const form = field.form || document.getElementById(field.getAttribute('form'));
      if (!form) return;

      const anyStillInvalid = form.querySelector('.artzy-field-invalid, .artzy-confirm-invalid');
      if (!anyStillInvalid) {
        this.renderValidationSummary(form, []);
      }
    }

    static initCharacterCounters(root) {
      root.querySelectorAll('[data-artzy-count-for]').forEach((counter) => {
        const field = document.getElementById(counter.dataset.artzyCountFor);
        if (!field) return;

        const update = () => {
          if (!field.maxLength || field.maxLength < 0) {
            return;
          }
          counter.textContent = `${field.value.length}/${field.maxLength}`;
        };

        field.addEventListener('input', update);
        update();
      });
    }
  }

  window.ArtzyPersonalization = ArtzyPersonalization;

  document.addEventListener('DOMContentLoaded', () => {
    ArtzyPersonalization.init();
  });

  document.addEventListener('shopify:section:load', (event) => {
    ArtzyPersonalization.init(event.target);
  });
})();
